import { forwardRef, useCallback, useEffect, useState } from 'react';

import { UniversalMenuItem } from '@klv/molecules/universal-menu-item';
import { UniversalMenuItemWithOptimizely } from '@klv/molecules/universal-menu-item/withOptimizely';
import { generateClassName } from '@klv/shared/utils/functions';

import { gtmClick } from '../../utils';
import { UniversalNavigationDrawer } from '../drawer';

import * as parentStyles from '../../styles/universalNavigation.module.css';
import * as styles from './styles/mobile.module.css';

import type { IUniversalNavigation } from '../../types';


type TOmittedFields = 'children' | 'color' | 'scrollDirection';

const MobileMenuItem = ({
  childItems,
  main,
  submenu,
}: {
  childItems?: IUniversalNavigation['menuItems'];
  main?: React.ReactNode;
  submenu?: React.ReactNode;
}) => childItems?.length ? (
  <details>
    <summary>
      {main}
    </summary>
    {submenu}
  </details>
) : main;

const Mobile = ({
  className,
  ctas = [],
  menuItems = [],
  menuStyle = 'default',
  path = '',
  platformActions = [],
  siteActions = [],
  utilityItems = [],
}: Omit<IUniversalNavigation, TOmittedFields>, ref: React.ForwardedRef<HTMLDialogElement>) => {
  const [hide, setHide] = useState(false);

  const onClose = useCallback(() => {
    if (typeof ref !== 'function' && ref?.current) {
      const dialog = ref.current;
      const animateOut = () => {
        setHide(false);
        dialog.close();
        dialog.removeEventListener('animationend', animateOut, false);
      };
      setHide(true);
      dialog.addEventListener('animationend', animateOut, false);
    }
  }, [ref]);

  useEffect(() => {
    if (typeof ref !== 'function' && ref?.current) {
      const listener = (event: MouseEvent | TouchEvent) => {
        if (
          event?.target
          && ref?.current
          && ref?.current?.open
          && (event?.target as HTMLElement)?.tagName === ref?.current?.tagName
        ) {
          onClose();
        }

        return;
      };

      document.addEventListener('mousedown', listener);
      document.addEventListener('touchstart', listener);

      return () => {
        document.removeEventListener('mousedown', listener);
        document.removeEventListener('touchstart', listener);
      };
    }
  }, [onClose, ref]);

  return (
    <dialog ref={ref} className={generateClassName([className, menuStyle === 'drilldown' && styles.drilldown, styles.mobileMenuDialog, hide && styles.hide])}>
      <div className={styles.closeContainer}>
        <button className={styles.close} onClick={() => onClose()} autoFocus />
      </div>
      <nav className={styles.nav}>
        <ul className={styles.list}>
          {menuItems.map(({ childItems, id, label, url: href = '' }) => (
            <li
              className={generateClassName([
                styles.listItem,
                childItems?.length && styles.hasChildren,
              ])}
              key={id}
            >
              <MobileMenuItem
                childItems={childItems}
                main={(
                  <UniversalMenuItemWithOptimizely
                    aria-current={
                      (childItems?.some(({ url }) => url === path) && 'true')
                      || href === path
                      || undefined
                    }
                    as={(href === '#' || href === '' || (menuStyle === 'drilldown' && childItems?.length)) ? 'div' : undefined}
                    className={generateClassName([
                      styles.link,
                      childItems?.length && styles.hasChildren,
                    ])}
                    featured={true}
                    href={(href === '#' || href === '' || (menuStyle === 'drilldown' && childItems?.length)) ? undefined : href}
                    onClick={() => { gtmClick({ childItem: label }); }}
                    optimizelyLabel={label}
                  >
                    <span>{label}</span>
                  </UniversalMenuItemWithOptimizely>
                )}
                submenu={
                  <UniversalNavigationDrawer
                    menuItems={childItems}
                    menuStyle={menuStyle}
                    parentLabel={label}
                  />
                }
              />
            </li>
          ))}
        </ul>
        <ul className={generateClassName([styles.list, styles.utilityList])}>
          {utilityItems.map(({ id, url, ...rest }) => (
            <li key={id}>
              <UniversalMenuItem
                className={generateClassName([
                  styles.utilityLink,
                ])}
                href={url}
                {...rest}
              />
            </li>
          ))}
          {siteActions.map(({ icon, label, ...rest }) => (
            <li key={label}>
              <UniversalMenuItem
                className={generateClassName([
                  styles.utilityLink,
                ])}
                iconName={icon}
                label={label}
                {...rest}
              />
            </li>
          ))}
        </ul>
        <ul className={generateClassName([styles.list, styles.ctaList])}>
          {ctas.map(({ id, variant, url, ...rest }) => (
            <li key={id}>
              <UniversalMenuItem
                className={generateClassName([
                  parentStyles.cta,
                  variant && parentStyles[variant],
                ])}
                href={url}
                {...rest}
              />
            </li>
          ))}
          {platformActions.map(({ component, label, ...rest }) => (
            <li key={label}>
              {component ? component : (
                <UniversalMenuItem
                  label={label}
                  {...rest}
                />
              )}
            </li>
          ))}
        </ul>
      </nav>
    </dialog>
  );
};

export const UniversalNavigationMobile = forwardRef(Mobile);
